"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { resolveApiUrl } from "@/data/runtime-data";

type MenuItem = {
  name: string;
  price: string;
  description?: string;
  imageUrl?: string;
};

type MenuSection = {
  title: string;
  items: MenuItem[];
};

interface MenuPageClientProps {
  sections: MenuSection[];
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  },
};

export default function MenuPageClient({ sections }: MenuPageClientProps) {
  const [liveSections, setLiveSections] = useState<MenuSection[]>(sections);
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null);
  const placeholderImageSrc = "/unchai-images/menu-placeholder.svg";

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(resolveApiUrl("/api/menu"), { cache: "no-store" });
        if (!res.ok) return;
        const json = (await res.json()) as { sections?: any[] };
        const nextSections = (json.sections || []).map((section: any) => ({
          title: section.name,
            items: (section.items || []).map((item: any) => ({
              name: item.name,
              price: item.price ?? "",
              description: item.description,
              imageUrl: item.imageUrl,
            })),
        })) as MenuSection[];
        if (nextSections.length) {
          setLiveSections(nextSections);
        }
      } catch (error) {
        console.warn("Failed to refresh menu from API, using initial data.", error);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (!activeItem) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveItem(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeItem]);

  return (
    <div className="bg-white">
      <section className="section pt-24 md:pt-32 text-gray-900 bg-gradient-to-b from-amber-50 via-orange-50 to-white">
        <div className="container max-w-5xl text-center">
          <p className="eyebrow text-amber-700">Our Menu</p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">Taste Unchai Thai Restaurant</h1>
          <p className="mt-4 text-lg md:text-xl text-gray-800 max-w-3xl mx-auto leading-relaxed">
            Thai appetizers, noodle soups, curries with rice, stir-fried noodles, rice dishes, and desserts.
            Prices and availability are subject to change. Call us to confirm specials and freshness.
          </p>
          <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-white border border-amber-200 px-4 py-3 shadow-sm">
            <span className="text-sm font-semibold text-amber-900">Spice key:</span>
            <span className="text-sm font-semibold text-gray-800 px-2 py-1 rounded-full bg-amber-50 border border-amber-100">
              * Mild
            </span>
            <span className="text-sm font-semibold text-gray-800 px-2 py-1 rounded-full bg-amber-50 border border-amber-100">
              ** Medium
            </span>
            <span className="text-sm font-semibold text-gray-800 px-2 py-1 rounded-full bg-amber-50 border border-amber-100">
              *** Spicy
            </span>
            <span className="text-sm font-semibold text-gray-800 px-2 py-1 rounded-full bg-amber-50 border border-amber-100">
              **** Very spicy
            </span>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {liveSections.map((section) => (
              <Link
                key={section.title}
                href={`#${section.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-2 rounded-full bg-white border border-amber-200 text-sm font-semibold text-amber-900 hover:border-amber-400"
              >
                {section.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container max-w-6xl space-y-12">
          {liveSections.map((section) => (
            <div key={section.title} id={section.title.toLowerCase().replace(/\s+/g, "-")}>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{section.title}</h2>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
                  {section.items.length} items
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {section.items.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    transition={{ delay: idx * 0.05, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                    className="card bg-white h-full border border-amber-100"
                  >
                    <div className="flex justify-between items-start gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">{item.price}</span>
                    </div>
                    {item.description && (
                      <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                    )}
                    {item.imageUrl && (
                      <div className="mt-4 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setActiveItem(item)}
                          className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900 hover:border-amber-300"
                        >
                          View photo
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          <div className="card bg-amber-50 border-amber-200">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Notes</h3>
            <p className="text-sm text-amber-900 leading-relaxed">
              Some stir-fried noodle dishes are best enjoyed fresh and are not recommended for takeout as the noodles
              may clump after 15 minutes. Please call us at (604) 559 6484 for the most up-to-date availability.
            </p>
          </div>
        </div>
      </section>

      {activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8"
          role="dialog"
          aria-modal="true"
          aria-label={`${activeItem.name} photo preview`}
          onClick={() => setActiveItem(null)}
        >
          <div
            className="relative flex w-full max-w-3xl flex-col rounded-2xl bg-white p-4 shadow-xl max-h-[90vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{activeItem.name}</h3>
                {activeItem.price && (
                  <p className="text-sm text-gray-600">{activeItem.price}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setActiveItem(null)}
                className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:border-gray-300"
              >
                Close
              </button>
            </div>
            <div className="mt-4 overflow-hidden rounded-xl bg-amber-50 h-72 md:h-96">
              <Image
                src={activeItem.imageUrl || placeholderImageSrc}
                alt={`${activeItem.name} preview`}
                width={900}
                height={600}
                className="h-full w-full object-contain"
                priority
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
